/**
 * Floating eat-toasts pool.
 *
 * On every player eat, a "+N" or "+N ×3" toast is spawned at the pellet's
 * world position. The toast rises slowly, fades out over ~1s, then recycles.
 * Fixed-size pool — no allocations after construction.
 */

interface Toast {
  alive: boolean;
  x: number;
  y: number;
  /** Floats up at this rate (world units / sec). */
  vy: number;
  /** Remaining life (sec). */
  life: number;
  /** Max life (sec) — used for the alpha fade. */
  maxLife: number;
  /** Pellet hue used to colorize the text. */
  hue: number;
  /** "+12" or "+36 ×3" — pre-formatted at spawn time. */
  text: string;
  /** True for color-match perfect eats (bigger text + accent treatment). */
  perfect: boolean;
}

export class ToastSystem {
  private readonly pool: Toast[];
  private cursor = 0;

  constructor(capacity = 64) {
    this.pool = new Array(capacity);
    for (let i = 0; i < capacity; i++) {
      this.pool[i] = {
        alive: false,
        x: 0, y: 0,
        vy: 0,
        life: 0, maxLife: 1,
        hue: 0,
        text: '',
        perfect: false,
      };
    }
  }

  spawn(opts: {
    x: number;
    y: number;
    hue: number;
    mass: number;
    multiplier: number;
    /** Optional pre-formatted text — overrides the auto "+N" / "+N ×3"
     *  output. Used by kill toasts to show "KILL · VEX" instead of mass. */
    text?: string;
    /** Optional life duration override (seconds). Used by kill toasts
     *  to linger a bit longer than eat toasts. */
    lifeOverride?: number;
  }): void {
    const t = this.pool[this.cursor]!;
    const isPerfect = opts.multiplier >= 2.5;
    const massInt = Math.round(opts.mass * opts.multiplier);
    t.alive = true;
    t.x = opts.x;
    t.y = opts.y - 12; // start slightly above the eat point
    t.vy = -34;        // rise (negative Y = up in canvas)
    t.life = opts.lifeOverride ?? (isPerfect ? 1.1 : 0.8);
    t.maxLife = t.life;
    t.hue = opts.hue;
    t.text = opts.text ?? (isPerfect ? `+${massInt} ×3` : `+${massInt}`);
    t.perfect = isPerfect;
    this.cursor = (this.cursor + 1) % this.pool.length;
  }

  step(dt: number): void {
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const t = pool[i]!;
      if (!t.alive) continue;
      t.y += t.vy * dt;
      // Subtle deceleration so the rise eases out.
      t.vy *= Math.exp(-0.9 * dt);
      t.life -= dt;
      if (t.life <= 0) t.alive = false;
    }
  }

  forEachAlive(fn: (t: Readonly<Toast>) => void): void {
    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const t = pool[i]!;
      if (t.alive) fn(t);
    }
  }
}

export type { Toast };
